import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from 'reactstrap';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import { withTranslation } from 'react-i18next';

import Breadcrumbs from '../../components/Common/Breadcrumb';
import { post } from '../../helpers/api_helper';
import { getAuthUser, isDebugUser } from '../../utilities/debugUsers';

function Debug(props) {
  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const { t } = props;
  const user = useMemo(() => getAuthUser(), []);
  const allowed = isDebugUser(user);

  const [stockId, setStockId] = useState('');
  const [lot, setLot] = useState('');
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await post('/api/warehouse/debug/available-location-pool', {
        stock_id: stockId,
        lot,
        qty: parseInt(qty, 10) || 1,
      });

      setResult(response);
      toastr.success(t('Debug query completed'));
    } catch (error) {
      setResult(null);
      toastr.error(error?.response?.data?.error || error.message || t('Unable to run debug query'), t('Error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title={t('Settings')} prevLink="/dashboard" breadcrumbItem={t('Debug')} />

        {!allowed && (
          <Row>
            <Col lg="8">
              <Alert color="danger" className="mb-0">
                {t('Unauthorized')}
              </Alert>
            </Col>
          </Row>
        )}

        {allowed && (
          <Row>
            <Col xl="5">
              <Card>
                <CardBody>
                  <CardTitle className="mb-3">{t('Debug Tools')}</CardTitle>
                  <p className="text-muted">
                    {t('Reserved for warehouse and system debugging.')}
                  </p>
                  <Form onSubmit={onSubmit}>
                    <FormGroup className="mb-3">
                      <Label for="stock_id">{t('Stock ID')}</Label>
                      <Input
                        id="stock_id"
                        name="stock_id"
                        value={stockId}
                        onChange={(e) => setStockId(e.target.value)}
                        required
                      />
                    </FormGroup>
                    <FormGroup className="mb-3">
                      <Label for="lot">{t('Lot')}</Label>
                      <Input
                        id="lot"
                        name="lot"
                        value={lot}
                        onChange={(e) => setLot(e.target.value)}
                        placeholder={t('Optional')}
                      />
                    </FormGroup>
                    <FormGroup className="mb-4">
                      <Label for="qty">{t('Qty')}</Label>
                      <Input
                        id="qty"
                        name="qty"
                        type="number"
                        min="1"
                        value={qty}
                        onChange={(e) => setQty(e.target.value)}
                      />
                    </FormGroup>
                    <Button color="primary" type="submit" disabled={loading}>
                      {loading ? <Spinner size="sm" /> : t('Test getAvailableLocationPool')}
                    </Button>
                  </Form>
                </CardBody>
              </Card>
            </Col>

            <Col xl="7">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <CardTitle className="mb-0">{t('Results')}</CardTitle>
                    {result && (
                      <Badge color="soft-primary">
                        {result.count}
                        {' '}
                        {t('locations')}
                      </Badge>
                    )}
                  </div>

                  {!result && (
                    <p className="text-muted mb-0">
                      {t('Run a test to inspect the available location pool.')}
                    </p>
                  )}

                  {result && (
                    <>
                      <div className="mb-3">
                        <strong>
                          {t('Inputs')}
                          :
                        </strong>
                        {' '}
                        <span className="text-muted">
                          stock_id=
                          {result.stock_id}
                          {', '}
                          lot=
                          {result.lot || 'null'}
                          {', '}
                          qty=
                          {result.qty}
                        </span>
                      </div>

                      <div className="table-responsive">
                        <Table className="align-middle table-nowrap mb-0">
                          <thead>
                            <tr>
                              <th>{t('Label')}</th>
                              <th>{t('Area')}</th>
                              <th>{t('Priority')}</th>
                              <th>{t('Stock Qty')}</th>
                              <th>{t('General Qty')}</th>
                              <th>{t('Full Qty')}</th>
                              <th>{t('Distinct Stocks')}</th>
                              <th>{t('Sequence')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.pool.length === 0 && (
                              <tr>
                                <td colSpan="8" className="text-muted">
                                  {t('No locations returned.')}
                                </td>
                              </tr>
                            )}
                            {result.pool.map((location) => (
                              <tr key={location.id}>
                                <td>
                                  <div className="fw-bold">{location.label}</div>
                                  <small className="text-muted">
                                    #
                                    {location.id}
                                  </small>
                                </td>
                                <td>{location.area_name || '-'}</td>
                                <td>{location.area_priority}</td>
                                <td>{location.stock_qty}</td>
                                <td>{location.general_qty}</td>
                                <td>{location.full_count}</td>
                                <td>{location.distinct_stock_count}</td>
                                <td>{location.sequence}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
}

Debug.propTypes = {
  t: PropTypes.any,
};

Debug.defaultProps = {
  t: (x) => (x),
};

export default connect(() => ({}), {})(withTranslation()(Debug));
